import { gsap } from "gsap";
import { SplitText } from "gsap/SplitText";

gsap.registerPlugin(SplitText);

export default function initGsapHeroSection() {
    const section = document.querySelector("#HeroSection");
    if (!section) return;

    // Use querySelectorAll to handle multiple logos (desktop/mobile)
    const heroLogos = section.querySelectorAll(".herosection__logo");
    const heroTitle = section.querySelector(".herosection__title");
    const heroVideo = section.querySelector(".herosection__video");

    // Skip if scrolled significantly
    if (window.scrollY > 100) return;

    // 1. Initial State
    const setInitialState = () => {
        // Logo starts hidden for entry animation
        if (heroLogos.length) {
            gsap.set(heroLogos, { 
                opacity: 0, 
                filter: "blur(0px)",
                scale: 0.8 // Start smaller
            });
        }

        // Title hidden initially
        if (heroTitle) {
            gsap.set(heroTitle, { opacity: 0 });
            // Split text setup
            const split = new SplitText(heroTitle, { type: "lines, words", linesClass: "overflow-hidden" });
            gsap.set(split.words, { y: 100, opacity: 0 });
            heroTitle._split = split;
        }

        // Video initial state
        if (heroVideo) {
            gsap.set(heroVideo, { filter: "blur(0px)" });
        }
    };

    setInitialState();

    // Helper for breathing animation
    const startBreathingLogo = () => {
        if (!heroLogos.length) return;
        // Kill existing tweens on logo to avoid conflicts
        gsap.killTweensOf(heroLogos);
        
        // Ensure we start from scale 1 (after entry)
        gsap.set(heroLogos, { scale: 1 });

        gsap.to(heroLogos, {
            scale: 1.15, 
            duration: 20, 
            repeat: -1,
            yoyo: true,
            ease: "sine.inOut"
        });
    };

    // 2. Animation Sequence
    const playIntroAnimation = () => {
        const tl = gsap.timeline({
            defaults: { ease: "power2.inOut" }, // Smoother ease for everything
            onComplete: () => {
                // Cleanup
                if (heroTitle && heroTitle._split) {
                    heroTitle._split.revert();
                }
                
                // Keep title visible (remove other props but ensure opacity and transform match CSS or visible state)
                if (heroTitle) {
                     gsap.set(heroTitle, { clearProps: "all" });
                     // Force opacity 1 via inline or class if CSS has it opacity 0
                     gsap.set(heroTitle, { opacity: 1 });
                }
                
                // Keep logo hidden
                if (heroLogos.length) {
                    gsap.set(heroLogos, { display: "none" }); 
                }

                // Ensure video keeps blur
                if (heroVideo) {
                    gsap.set(heroVideo, { filter: "blur(5px)" });
                }
            }
        });

        // Define a common start time for the "push" effect
        tl.add("pushStart");

        // Step 1: Animate Logo Out (Moves UP dramatically)
        if (heroLogos.length) {
            tl.to(heroLogos, {
                opacity: 0,
                scale: 1.5,
                y: -250, // Move up significantly to feel "pushed"
                filter: "blur(20px)",
                duration: 2, 
                ease: "power2.inOut"
            }, "pushStart");
        }

        // Step 2: Animate Title In (Moves UP filling the space)
        if (heroTitle && heroTitle._split) {
            // Make container visible immediately at start of sequence (words are still hidden)
            tl.set(heroTitle, { opacity: 1 }, "pushStart");
            
            tl.to(heroTitle._split.words, {
                y: 0,
                opacity: 1,
                duration: 2,
                stagger: 0.1,
                ease: "power2.inOut" // Match logo easing for connected feel
            }, "pushStart+=0.1"); // Start almost simultaneously with logo
        }

        // Step 3: Animate Video Blur (Strictly sync with title start)
        if (heroVideo) {
            tl.to(heroVideo, {
                filter: "blur(5px)",
                scale: 1.1, // Slight zoom in
                duration: 2,
                ease: "power2.inOut"
            }, "pushStart+=0.1"); // Sync with text
        }
    };

    // 3. Event Listener
    window.addEventListener("vantys:loader:start-fade", () => {
        // Double check scroll position
        if (window.scrollY > 100) {
            // Revert immediately if scrolled
            if (heroLogos.length) gsap.set(heroLogos, { display: "none" });
            if (heroTitle) {
                gsap.set(heroTitle, { opacity: 1 });
                if (heroTitle._split) heroTitle._split.revert();
            }
            return;
        }

        // 3a. Logo Entry Animation immediately on loader fade
        if (heroLogos.length) {
            gsap.to(heroLogos, {
                opacity: 1,
                scale: 1,
                duration: 1.5,
                ease: "power2.out",
                onComplete: startBreathingLogo // Start breathing after entry
            });
        }

        // Logic: Min wait 2s, Max wait 5s.
        // If interaction < 2s: queued, triggers at 2s.
        // If interaction > 2s: triggers immediately.
        // If no interaction: triggers at 5s.
        
        const MIN_WAIT = 1200;
        const MAX_WAIT = 3500;

        let minWaitComplete = false;
        let queuedInteraction = false;
        let animationStarted = false;

        const attemptTrigger = () => {
            if (animationStarted) return;
            // Only trigger if minimal wait is over
            if (minWaitComplete) {
                animationStarted = true;
                
                // Cleanup
                ["scroll", "mousemove", "touchstart", "wheel", "click", "keydown"].forEach(event => {
                    window.removeEventListener(event, handleInteraction);
                });
                if (heroLogos.length) gsap.killTweensOf(heroLogos);
                
                playIntroAnimation();
            }
        };

        const handleInteraction = () => {
            queuedInteraction = true;
            attemptTrigger();
        };

        // 1. Setup Listeners immediately to catch early interactions
        ["scroll", "mousemove", "touchstart", "wheel", "click", "keydown"].forEach(event => {
            window.addEventListener(event, handleInteraction, { once: true });
        });

        // 2. Minimum Wait Timer
        setTimeout(() => {
            minWaitComplete = true;
            if (queuedInteraction) {
                attemptTrigger();
            }
        }, MIN_WAIT);

        // 3. Maximum Wait Timer (Auto-play)
        setTimeout(() => {
            if (!animationStarted) {
                // Force trigger even if no interaction (simulate queued interaction and wait complete)
                minWaitComplete = true; 
                attemptTrigger();
            }
        }, MAX_WAIT);

    }, { once: true });
}
